// src/pages/Numbers/index.jsx
// STATUS: active | UPDATED: 2026-09-23
// LOG:
//   2026-09-23  New. The fourth leg of the governing test finally has a screen.
//
// WHERE A STORE REPORTS ITS MONTH.
//
// "Help the dealership measure and sustain it" is one of the four things page
// 19 says a feature must do, and it was the only one with nothing behind it.
// The tables survived the 0013 cull and sat empty for seven weeks because
// seeding them before Jazz answered would have been inventing his business.
//
// FIVE FIELDS AND NOTHING ELSE. He named five numbers and this screen asks for
// exactly those. The temptation with a page like this is to add appointment
// counts, CSI, close rate, everything a dealership measures. All of that is
// somebody else's idea of a scorecard. If a sixth number belongs here, it comes
// out of his mouth first.
//
// OWNER ONLY, and the route enforces it. 0038 enforces it again in the
// database, which is the one that actually matters.
//
// NO FAKE DATA. A store that has reported nothing sees an empty state that says
// so. The 2026-08-02 no placeholder revamp removed invented numbers everywhere
// for the reason this screen makes obvious: a salesperson who catches the app
// lying once will not trust the number that matters later, and this is the
// table whose entire job is to be true.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, HStack, VStack, Text, Input, Button, Spinner, Alert, AlertIcon, IconButton, Badge, InputGroup, InputLeftElement,
} from '@chakra-ui/react'
import { ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import {
  listDefinitions, listEntries, saveMonth, lastCompleteMonth, shiftMonth, monthLabel,
  growth, growthBand, formatValue, GROWTH_FLOOR, GROWTH_STRETCH,
} from '../../lib/kpi'

const TREND_MONTHS = 6

const BAND = {
  stretch: { color: 'success', icon: TrendingUp },
  met: { color: 'success', icon: TrendingUp },
  short: { color: 'warn', icon: Minus },
  down: { color: 'danger', icon: TrendingDown },
  none: { color: 'inkDim', icon: Minus },
}

function GrowthBadge({ pct }) {
  if (pct == null) return null
  const band = growthBand(pct)
  const { color, icon: Icon } = BAND[band]
  return (
    <HStack spacing={1} color={color} fontFamily="mono" fontSize="xs" whiteSpace="nowrap">
      <Icon size={13} aria-hidden="true" />
      <Text as="span">
        {pct > 0 ? '+' : ''}
        {pct.toFixed(1)}%
      </Text>
    </HStack>
  )
}

export default function Numbers() {
  const { user, profile } = useAuth()
  const dealershipId = profile?.dealership_id || null

  const [month, setMonth] = useState(() => lastCompleteMonth())
  const [defs, setDefs] = useState([])
  const [entries, setEntries] = useState([])
  const [draft, setDraft] = useState({})
  const [state, setState] = useState({ loading: true, saving: false, error: null, saved: false })

  const since = useMemo(() => shiftMonth(month, -(TREND_MONTHS - 1)), [month])

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    const [d, e] = await Promise.all([listDefinitions(), listEntries(dealershipId, since)])
    const err = d.error || e.error
    setDefs(d.data)
    setEntries(e.data)
    // The draft is seeded from what is already stored for the month on screen,
    // so opening a reported month shows what was reported rather than blanks.
    const seed = {}
    for (const row of e.data) {
      if (row.period_month === month) seed[row.kpi_definition_id] = String(row.value)
    }
    setDraft(seed)
    setState({ loading: false, saving: false, error: err ? err.message || String(err) : null, saved: false })
  }, [dealershipId, since, month])

  useEffect(() => {
    load()
  }, [load])

  const byMonth = useMemo(() => {
    const map = {}
    for (const row of entries) {
      map[row.period_month] = map[row.period_month] || {}
      map[row.period_month][row.kpi_definition_id] = row.value
    }
    return map
  }, [entries])

  const trendMonths = useMemo(() => {
    const out = []
    for (let i = TREND_MONTHS - 1; i >= 0; i -= 1) out.push(shiftMonth(month, -i))
    return out
  }, [month])

  const anyReported = entries.length > 0

  const save = async () => {
    setState((s) => ({ ...s, saving: true, error: null, saved: false }))
    const { error } = await saveMonth({ dealershipId, periodMonth: month, values: draft, userId: user?.id })
    if (error) {
      setState((s) => ({ ...s, saving: false, error }))
      return
    }
    await load()
    setState((s) => ({ ...s, saved: true }))
  }

  if (state.loading) {
    return (
      <VStack align="stretch" maxW="sheet" spacing={6}>
        <Spinner size="sm" />
      </VStack>
    )
  }

  return (
    <VStack align="stretch" spacing={10} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          For the person who owns the store
        </Text>
        <Text textStyle="pageTitle">The numbers</Text>
        <Text fontSize="lg" color="inkMuted" mt={2} maxW="measure" lineHeight={1.55}>
          The five Jazz asks a store to report every month. He is looking for at least{' '}
          {GROWTH_FLOOR} to {GROWTH_STRETCH} percent growth on each of them, month over month.
        </Text>
      </Box>

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {!dealershipId && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          Your account is not attached to a store, so there is nowhere to file these.
        </Alert>
      )}

      <Box>
        <HStack justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
          <HStack spacing={1}>
            <IconButton
              aria-label="Previous month"
              icon={<ChevronLeft size={16} />}
              size="sm"
              variant="ghost"
              onClick={() => setMonth((m) => shiftMonth(m, -1))}
            />
            <Text fontSize="lg" fontWeight={600} minW="11ch" textAlign="center">
              {monthLabel(month)}
            </Text>
            <IconButton
              aria-label="Next month"
              icon={<ChevronRight size={16} />}
              size="sm"
              variant="ghost"
              isDisabled={month >= lastCompleteMonth()}
              onClick={() => setMonth((m) => shiftMonth(m, 1))}
            />
          </HStack>
          <Text fontSize="sm" color="inkDim" fontFamily="mono">
            {state.saving ? 'saving' : state.saved ? 'saved' : ''}
          </Text>
        </HStack>

        <VStack align="stretch" spacing={0}>
          {defs.map((def) => {
            const prev = byMonth[shiftMonth(month, -1)]?.[def.id]
            const current = Number(String(draft[def.id] ?? '').replace(/[$,\s]/g, ''))
            const pct = growth(current, prev)
            return (
              <Box key={def.id} py={4} borderTop="1px solid" borderColor="line">
                <HStack justify="space-between" align="baseline" mb={1} flexWrap="wrap" gap={2}>
                  <Text fontWeight={600} fontSize="body">
                    {def.name}
                  </Text>
                  <GrowthBadge pct={pct} />
                </HStack>
                <HStack align="center" spacing={3} flexWrap="wrap">
                  <InputGroup maxW="15rem">
                    {def.unit === 'dollars' && (
                      <InputLeftElement pointerEvents="none" color="inkDim" fontSize="body">
                        $
                      </InputLeftElement>
                    )}
                    <Input
                      id={`kpi-${def.slug}`}
                      value={draft[def.id] ?? ''}
                      onChange={(e) => {
                        setDraft((d) => ({ ...d, [def.id]: e.target.value }))
                        setState((s) => ({ ...s, saved: false }))
                      }}
                      placeholder="not reported"
                      inputMode="decimal"
                      fontFamily="mono"
                      pl={def.unit === 'dollars' ? 8 : undefined}
                    />
                  </InputGroup>
                  {Number.isFinite(prev) && (
                    <Text fontSize="sm" color="inkDim" fontFamily="mono">
                      last month {formatValue(def.unit, prev)}
                    </Text>
                  )}
                </HStack>
                <Text fontSize="sm" color="inkMuted" mt={2} maxW="measure" lineHeight={1.55}>
                  {def.description}
                </Text>
              </Box>
            )
          })}
        </VStack>

        {/* Wraps rather than squeezing. The helper text and the button fight
            for one line below about 640px, and the text loses first. */}
        <HStack mt={5} spacing={3} align="center" flexWrap="wrap" rowGap={2}>
          <Button onClick={save} isLoading={state.saving} isDisabled={!dealershipId || defs.length === 0}>
            Save {monthLabel(month)}
          </Button>
          <Text fontSize="sm" color="inkDim" maxW="measure" flex="1 1 18rem">
            Leave one blank if you do not have it. Blank is different from zero and it is stored that way.
          </Text>
        </HStack>
      </Box>

      <Box>
        <Text fontSize="lg" fontWeight={600} mb={0.5}>
          The last six months
        </Text>
        {!anyReported ? (
          <Text fontSize="body" color="inkMuted" maxW="measure" lineHeight={1.6}>
            Nothing reported yet. This fills in as you file months, and the point of it is the change
            between them rather than any single figure, so it earns its place from the second month on.
          </Text>
        ) : (
          <Box overflowX="auto" mt={2}>
            <Box as="table" width="100%" style={{ borderCollapse: 'collapse' }} fontSize="sm">
              <Box as="thead">
                <Box as="tr">
                  <Box as="th" textAlign="left" pb={2} pr={4} color="inkDim" fontFamily="mono" fontSize="xs" fontWeight={600} borderBottom="1px solid" borderColor="line">
                    METRIC
                  </Box>
                  {trendMonths.map((m) => (
                    <Box key={m} as="th" textAlign="right" pb={2} pl={4} color="inkDim" fontFamily="mono" fontSize="xs" fontWeight={600} whiteSpace="nowrap" borderBottom="1px solid" borderColor="line">
                      {monthLabel(m).replace(/ \d{4}$/, '')}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box as="tbody">
                {defs.map((def) => (
                  <Box as="tr" key={def.id}>
                    <Box as="td" py={3} pr={4} borderBottom="1px solid" borderColor="line" fontWeight={600}>
                      {def.name}
                    </Box>
                    {trendMonths.map((m, i) => {
                      const v = byMonth[m]?.[def.id]
                      const p = i > 0 ? byMonth[trendMonths[i - 1]]?.[def.id] : undefined
                      const pct = growth(v, p)
                      return (
                        <Box key={m} as="td" py={3} pl={4} textAlign="right" borderBottom="1px solid" borderColor="line" fontFamily="mono" whiteSpace="nowrap">
                          {Number.isFinite(v) ? (
                            <VStack spacing={0} align="flex-end">
                              <Text as="span">{formatValue(def.unit, v)}</Text>
                              <GrowthBadge pct={pct} />
                            </VStack>
                          ) : (
                            <Text as="span" color="inkDim">
                              not reported
                            </Text>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box>
        <Badge variant="subtle" fontFamily="mono" fontSize="xs" letterSpacing="0.06em">
          NOT YET LINKED
        </Badge>
        <Text fontSize="body" color="inkMuted" mt={2} maxW="measure" lineHeight={1.6}>
          Each of these is supposed to point at the steps that move it, which is the whole J13 argument
          as a piece of the database. That link is Jazz&rsquo;s to draw and he has not drawn it yet.
        </Text>
      </Box>
    </VStack>
  )
}
